
import { trackTikTokEvent } from './tiktokPixel';

/**
 * Facebook Pixel Standard Events Reference:
 * - PageView: Track page views (auto-tracked)
 * - ViewContent: Track product/content views
 * - Search: Track search queries
 * - AddToCart: Track when items added to cart
 * - AddToWishlist: Track when items added to wishlist
 * - InitiateCheckout: Track checkout initiations
 * - AddPaymentInfo: Track payment info submissions
 * - Purchase: Track completed purchases
 * - Lead: Track leads/sign-ups
 * - CompleteRegistration: Track registration completions
 */

/**
 * Safely track Facebook Pixel events with retry mechanism.
 * Ensures events are fired even if the Pixel script loads slightly after the component.
 */
export const trackFacebookEvent = (eventName: string, data?: Record<string, any>): Promise<void> => {
  const MAX_RETRIES = 3; // Quick check — if pixel isn't loaded after 300ms, it's not configured
  let attempts = 0;

  // Add event_id for deduplication
  const eventId = `${eventName}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  const eventData = { ...data, event_id: eventId };

  // Map Facebook event name to TikTok event name
  let ttEventName = eventName;
  if (eventName === 'Purchase') ttEventName = 'PlaceAnOrder';
  
  // Map Facebook data structure to TikTok data structure
  const ttData: any = { ...data };
  if (data?.content_ids && Array.isArray(data.content_ids)) {
    const quantity = data.num_items || 1;
    ttData.contents = data.content_ids.map((id: string) => ({
      content_id: id,
      content_type: data.content_type || 'product',
      content_name: data.content_name || '',
      price: data.value ? (data.value / quantity) : 0,
      quantity: quantity
    }));
  }
  if (data?.search_string) ttData.query = data.search_string;

  // Track TikTok event concurrently
  trackTikTokEvent(ttEventName, ttData, { event_id: eventId }).catch(e => console.error(e));

  return new Promise((resolve) => {
    const track = () => {
      if (window.fbq) {
        window.fbq('track', eventName, eventData);
        resolve();
      } else if (attempts < MAX_RETRIES) {
        attempts++;
        setTimeout(track, 100);
      } else {
        // FB Pixel not configured — silently resolve
        resolve();
      }
    };

    track();
  });
};

/**
 * Track a custom event (non-standard Facebook event)
 */
export const trackCustomEvent = (eventName: string, data?: Record<string, any>): Promise<void> => {
  const MAX_RETRIES = 3;
  let attempts = 0;

  return new Promise((resolve) => {
    const track = () => {
      if (window.fbq) {
        const eventData = {
          ...data,
          event_id: `${eventName}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        };
        
        window.fbq('trackCustom', eventName, eventData);
        resolve();
      } else if (attempts < MAX_RETRIES) {
        attempts++;
        setTimeout(track, 100);
      } else {
        // FB Pixel not configured — silently resolve
        resolve();
      }
    };

    track();
  });
};

/**
 * Predefined event helpers for common e-commerce actions
 */
export const fbEvents = {
  viewContent: (product: {
    id: string | number;
    name: string;
    price: number;
    category?: string;
  }) => trackFacebookEvent('ViewContent', {
    content_name: product.name,
    content_ids: [product.id.toString()],
    content_type: 'product',
    value: product.price,
    currency: 'BDT',
    content_category: product.category || ''
  }),

  addToCart: (product: {
    id: string | number;
    name: string;
    price: number;
    quantity?: number;
  }) => trackFacebookEvent('AddToCart', {
    content_name: product.name,
    content_ids: [product.id.toString()],
    content_type: 'product',
    value: product.price * (product.quantity || 1),
    currency: 'BDT',
    num_items: product.quantity || 1
  }),

  addToWishlist: (product: {
    id: string | number;
    name: string;
    price: number;
  }) => trackFacebookEvent('AddToWishlist', {
    content_name: product.name,
    content_ids: [product.id.toString()],
    content_type: 'product',
    value: product.price,
    currency: 'BDT'
  }),

  initiateCheckout: (items: Array<{
    id: string | number;
    quantity: number;
  }>, totalValue: number) => trackFacebookEvent('InitiateCheckout', {
    content_ids: items.map(item => item.id.toString()),
    content_type: 'product',
    value: totalValue,
    currency: 'BDT',
    num_items: items.reduce((acc, item) => acc + item.quantity, 0)
  }),

  purchase: (order: {
    orderId: string;
    items: Array<{ id: string | number; quantity: number }>;
    totalValue: number;
  }) => trackFacebookEvent('Purchase', {
    content_ids: order.items.map(item => item.id.toString()),
    content_type: 'product',
    value: order.totalValue,
    currency: 'BDT',
    num_items: order.items.reduce((acc, item) => acc + item.quantity, 0),
    order_id: order.orderId
  }),

  search: (searchQuery: string) => trackFacebookEvent('Search', {
    search_string: searchQuery,
    content_type: 'product'
  })
};
